import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';

import { retryFailedCall } from '@common';

import { AppInsightsMonitoringService } from 'src/app/shared/services/app-insights/app-insights-monitoring.service';
import { APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP } from 'src/app/shared/services/app-insights/events/remote-diagnostics.events';
import { LogLevelEnum } from 'src/app/shared/services/app-insights/app-insights-monitoring.service';

import { RemoteDiagnosticsCloudApiPicker } from '../../../projects/client';
import { InstallationDetailsStore } from '../installation-details';
import { RemoteDiagnosticsStore } from '../remote-diagnostics';

import { DiagStackDtcActions } from './diag-stack-dtc.actions';

@Injectable()
export class DiagStackDtcEffects {
  getActiveDtc$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(DiagStackDtcActions.getActiveDtc.attempt),
      concatLatestFrom(() => this.installationDetailsStore.gatewaySerial$),
      mergeMap(([action, gatewaySerial]) =>
        combineLatest([
          of(action),
          this.remoteDiagnosticsStore.getDiagStackConnectionProperties(gatewaySerial).pipe(take(1)),
          this.remoteDiagnosticsStore.getMainLogicalLinkFromTopology(gatewaySerial).pipe(take(1)),
        ])
      ),
      switchMap(([action, diagStackConnectionProperties, mainLogicalLinkFromTopology]) =>
        this.remoteDiagnosticsCloudApiPicker
          .instance()
          .diagnosticServiceDtcActive(
            diagStackConnectionProperties.node,
            diagStackConnectionProperties.xAuthToken,
            mainLogicalLinkFromTopology,
            diagStackConnectionProperties.vciName,
            diagStackConnectionProperties.vciResource
          )
          .pipe(
            retryFailedCall(3),
            tap(() => {
              this.appInsights.logEvent(
                APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.GET_ACTIVE_DTC_SUCCESS,
                LogLevelEnum.TRACE
              );
            }),
            map((activeDtcResponse) =>
              DiagStackDtcActions.getActiveDtc.success({
                gatewaySerial: action.gatewaySerial,
                activeDtcResponse,
              })
            ),
            catchError((error) => {
              this.appInsights.logEvent(
                APP_INSIGHTS_DIAGNOSTICS_CONNECTION_STEP.GET_ACTIVE_DTC_FAILED,
                LogLevelEnum.ERROR,
                { error }
              );

              return of(
                DiagStackDtcActions.getActiveDtc.error({
                  gatewaySerial: action.gatewaySerial,
                  activeDtcError: error,
                })
              );
            })
          )
      )
    );
  });

  constructor(
    private actions$: Actions,
    private installationDetailsStore: InstallationDetailsStore,
    private remoteDiagnosticsCloudApiPicker: RemoteDiagnosticsCloudApiPicker,
    private remoteDiagnosticsStore: RemoteDiagnosticsStore,
    private appInsights: AppInsightsMonitoringService
  ) {}
}
